// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection/AccordionSection";
import ManagedAccordionSection from "@insite/mobius/AccordionSection/ManagedAccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll from "@pace/App/Components/Common/FancyScroll";
import { CategoryDetailsContext } from "@pace/App/Store/Context/CategoryDetailsContext";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasCategoryContext & HasHistory;

export interface CategoryDetailLinkListStyles {
    container?: GridContainerProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // XNG-Changes
    accordionSection?: AccordionSectionPresentationProps;
    subCategoryWrapper?: InjectableCss;
    accordionWrapper?: InjectableCss;
    subcategoryListHeaderGridItem?: GridItemProps;
    subcategoryFiltersHeaderText?: TypographyPresentationProps;
    categoryDropdownGridItem?: GridItemProps;
}

export const categoryDetailLinkListStyles: CategoryDetailLinkListStyles = {
    container: {
        gap: 0,
    },
    subCategoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 10px;
        `,
    },
};

const styles = categoryDetailLinkListStyles;

const CategoryDetailLinkList = ({ category }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            {/** XNG-Changes */}
            <GridItem {...styles.subcategoryListHeaderGridItem}>
                <Typography {...styles.subcategoryFiltersHeaderText}>{translate("Categories")}</Typography>
            </GridItem>

            {category.subCategoryIds.map((subCategoryId, index) => (
                <SubCategoryLink key={subCategoryId.toString()} subCategoryId={subCategoryId} index={index} />
            ))}
        </GridContainer>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { subCategoryId: string; index: number }) => {
    const category = getCategoryState(state, ownProps.subCategoryId).value;
    return {
        shortDescription: category?.shortDescription || "",
        subCategories: category?.subCategoryIds?.map(id => getCategoryState(state, id).value),
        index: ownProps.index,
    };
};

const SubCategoryLinkView = ({ shortDescription, subCategories, index }: ReturnType<typeof mapStateToProps>) => {
    const parentElement = React.createRef<HTMLDivElement>();
    const { hasSubcategories, setHasSubCategories } = React.useContext(CategoryDetailsContext);
    if (!subCategories?.length) {
        return null;
    }

    // XNG-Changes
    if (subCategories?.length && !hasSubcategories) {
        setHasSubCategories(true);
    }

    return (
        <GridItem {...styles.subCategoryNameLinkItem}>
            <StyledWrapper {...styles.accordionWrapper} className="accordion-wrapper">
                <Accordion headingLevel={2}>
                    <ManagedAccordionSection
                        title={shortDescription}
                        {...styles.accordionSection}
                        initialExpanded={index === 0}
                    >
                        <FancyScroll refElement={parentElement} color="common.ui1" data-test-selector="sth">
                            <StyledWrapper {...styles.subCategoryWrapper} ref={parentElement}>
                                {subCategories?.map(subCategory => (
                                    <>
                                        <Link
                                            href={subCategory?.path}
                                            {...styles.subCategoryNameLink}
                                            data-test-selector={`categoryDetailsLinkListLink_${subCategory?.id}`}
                                            aria-label={subCategory?.shortDescription}
                                            key={subCategory?.id}
                                        >
                                            {subCategory?.shortDescription}
                                        </Link>
                                    </>
                                ))}
                            </StyledWrapper>
                        </FancyScroll>
                    </ManagedAccordionSection>
                </Accordion>
            </StyledWrapper>
        </GridItem>
    );
};

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailLinkList),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
